import Avo from 'analytics/Avo';
import { useConfig } from 'context/app-config';
import { useUpdateCouponCode } from 'pages/queries/order.queries';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectCustomer, setActiveCampaigns, setPromoCode, setUserDetails } from 'redux-stores/slices/authSlice';
import type { AppDispatch } from 'redux-stores/store';

import type { IPromoField, PromoCodeForm } from '../types';

export function usePromoCode(props: IPromoField) {
  const { t } = useTranslation();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const customer = useSelector(selectCustomer);
  const { onClose, show } = props;
  const { mutateAsync: addPromoCode, isLoading } = useUpdateCouponCode();
  const dispatch = useDispatch<AppDispatch>();
  const {
    register,
    setValue,
    errors,
    handleSubmit,
    setError,
    formState,
    control,
    reset: resetForm,
    clearErrors,
  } = useForm<PromoCodeForm>({
    mode: 'all',
  });

  const config = useConfig();

  const reset = useCallback(() => {
    resetForm({ promocode: '' });
    clearErrors('promocode');
    setSuccessMessage('');
    setErrorMessage('');
  }, [resetForm]);

  const showSuccessMessage = useCallback(() => {
    switch (config?.modules.checkout.promptType) {
      case 'status':
        setSuccessMessage(t(config?.modules.checkout.prompts.success!!, 'You have successfully redeemed'));
        break;
      default:
        toast.dark(t(config?.modules.checkout.prompts.success!!));
    }
  }, [config?.modules.checkout.promptType, config?.modules.checkout.prompts.success, t]);

  const submitHandler = useCallback(
    async (data: PromoCodeForm) => {
      if (!successMessage) {
        const activeCampaigns = customer?.activeCampaigns ?? [];
        addPromoCode(data.promocode)
          .then((res) => {
            Avo.usedPromocode({ promocode: data.promocode });
            showSuccessMessage();
            dispatch(setPromoCode(data.promocode));
            dispatch(setUserDetails(res.data.updatedUser));
            dispatch(setActiveCampaigns([...activeCampaigns, { campaignId: data.promocode, customerMessage: res.data?.messageNotification ?? '' }]));
            if (props.resetOnSuccess) {
              reset();
            }
            onClose?.();
          })
          .catch((e) => {
            const message = e?.response?.data?.infoCode?.message || t('newOrderPage.somethingWrongTryLaterMessage');
            setError('promocode', {
              type: 'server',
              message,
            });
            setErrorMessage(message);
          });
      }
    },
    [successMessage, addPromoCode, showSuccessMessage, dispatch, onClose, t, setError]
  );

  return {
    submitHandler,
    register,
    handleSubmit,
    successMessage,
    formState,
    errors,
    control,
    show,
    isLoading,
    errorMessage,
    reset,
    setValue,
  };
}
